import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Space } from '@app/core/model/entities/asset/space';
import { Actor } from '@app/core/model/entities/entity';
import { AssetGroupsService } from '@app/features/main/views/asset-groups/asset-groups.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { EquipmentsService } from '@app/features/main/views/equipments/equipments.service';
import { ProjectsService } from '@app/features/main/views/projects/projects.service';
import { WorksService } from '@app/features/main/views/works/works.service';
import { AbstractFieldBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { plainToInstance } from 'class-transformer';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'computed-field-builder',
  templateUrl: './computed-field-builder.component.html',
  styleUrls: ['./computed-field-builder.component.scss'],
  standalone: false
})
export class ComputedFieldBuilderComponent extends AbstractFieldBuilder<FormControl> implements OnInit, AfterViewInit {

  public Permission = PermissionEnum;

  /**
   * Key is id of entity's parents and value is name of entity's parent
   */
  public parentNames = new Map<string, string>();

  private assetsService = inject(AssetsService);
  private equipmentsService = inject(EquipmentsService);
  private projectsService = inject(ProjectsService);
  private assetGroupsService = inject(AssetGroupsService);
  private worksService = inject(WorksService);

  /**
   * Initialise the form.
   */
  public ngOnInit(): void {
    this.formatValue();

    this.form = new FormGroup({
      field: new FormControl(
        {value: this.fieldInitValue, disabled: true}
      )
    });
    this.getNextState();

    // Initialise parentNames used to display spaces' name
    this.entity['parentPath']?.forEach((parentId, i) => {
      this.parentNames.set(parentId, (this.entity as Space).hierarchy?.[i]);
    });
  }

  /**
   * Set up hooks and listen to formStateService changes.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();

    if (this.isSingleField) {
      this.getFieldGroupState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newMode) => {
        if (newMode === FieldStateMode.EMPTY) {
          this.read();
        }
      });
    }

    this.formStateService.getPath(['computed']).pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.entity = this.appManager.currentEntity;
      this.calcFieldInitValue();
      this.formatValue();
      this.form.controls.field.setValue(this.fieldInitValue);

      if (this.fieldInitValue !== null && this.fieldInitValue !== void 0) {
        this.getFieldGroupInfo(['isDisplayed']).next(true);
      }
    });
  }

  /**
   * Get the field value converted to a string and translated if the 'translate' flag in the custom options is set.
   * @return string
   */
  public getFieldValue(): string {
    const value = this.form.controls.field.value;
    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'VALUE.TRUE' : 'VALUE.FALSE');
    } else {
      return value?.toString()
        ?.let((it: string) => this.fieldConfig.customOptions?.translate ? this.translateValue(it) : this.translate.instant(it));
    }
  }

  /**
   * Navigate to the sheet of the related Entity.
   * @throws Error if the entity type is not supported.
   */
  public navigateToEntitySheet(): Promise<void | boolean> {
    switch (this.entity['type'] as EntityTypeEnum) {
      case EntityTypeEnum.ASSET:
        return this.assetsService.navigateToAssetSheet(this.entity['entityId'], 'dashboard');
      case EntityTypeEnum.WORK:
        return this.worksService.navigateToWorkSheet(this.entity['entityId']);
      case EntityTypeEnum.EQUIPMENT:
        return this.equipmentsService.navigateToEquipmentSheet(this.entity['entityId']);
      case EntityTypeEnum.PROJECT:
        return this.projectsService.navigateToProjectSheet(this.entity['entityId']);
      case EntityTypeEnum.ASSET_GROUP:
        return this.assetGroupsService.navigateToAssetGroupSheet(this.entity['entityId']);
      default:
        throw new Error(`Entity type ${this.entity['type']} not supported`);
    }
  }

  /**
   * Unused in the context of computed fields since there are not editable.
   */
  public cancel(): void {
    // Do nothing
  }

  /**
   * Format initial value of the form, depending on the field type.
   * @private
   */
  private formatValue(): void {
    if (this.fieldInitValue) {
      switch (this.fieldConfig.field.fieldType) {
        case FieldTypeEnum.DATE:
        case FieldTypeEnum.DATETIME:
          this.fieldInitValue = this.appConfig.getFormattedDate(this.fieldInitValue, this.fieldConfig.field.fieldType);
          break;
        case FieldTypeEnum.ACTOR:
          this.fieldInitValue = plainToInstance(Actor, this.fieldInitValue).toString();
          break;
      }
    }
  }
}
